import { products, type GroupId, type ProductGroup } from '../presentation/catalogue';
import type { PresentationContext } from '../presentation/types';
import { brandLogoSvg } from './brandLogo';

/**
 * Shared geometry for every ecosystem view (ecosystem, growing ecosystem, closing). The layout is
 * computed from whichever product groups are enabled, so removing a product never leaves a gap.
 */
export const ECO = { cx: 800, cy: 450, rx: 560, ry: 290, coreRadius: 96 } as const;

export interface Anchor { x: number; y: number; angle: number }

const anchorAt = (deg: number): Anchor => {
  const a = (deg * Math.PI) / 180;
  return { x: ECO.cx + Math.cos(a) * ECO.rx, y: ECO.cy + Math.sin(a) * ECO.ry, angle: deg };
};

/** Angles spread evenly across [from, to], or the midpoint for a single item. */
const spread = (count: number, from: number, to: number): number[] =>
  Array.from({ length: count }, (_, i) => (count === 1 ? (from + to) / 2 : from + (i * (to - from)) / (count - 1)));

/**
 * The layout carries the hierarchy. MINT, the flagship, holds the left of the orbit on its own;
 * major and independent products share an arc on the right; selected work sits low and small.
 * Without the flagship, the remaining groups are spaced evenly so there is never a hole.
 */
export function layoutGroups(list: readonly ProductGroup[]): Map<GroupId, Anchor> {
  const map = new Map<GroupId, Anchor>();
  const flagship = list.filter((g) => g.tier === 'flagship');
  if (!flagship.length) {
    list.forEach((group, i) => map.set(group.id, anchorAt(list.length === 1 ? -90 : -150 + (i * 360) / list.length)));
    return map;
  }
  const independent = list.filter((g) => g.tier === 'major' || g.tier === 'independent');
  const selected = list.filter((g) => g.tier === 'selected');
  flagship.forEach((g) => map.set(g.id, anchorAt(180)));
  const arc = independent.length > 3 ? 70 : 50;
  spread(independent.length, -arc, arc).forEach((deg, i) => map.set(independent[i].id, anchorAt(deg)));
  spread(selected.length, 112, 132).forEach((deg, i) => map.set(selected[i].id, anchorAt(deg)));
  return map;
}

/**
 * Each group is named by its own master logo (mini-lockup), never by a retyped name or an invented
 * symbol. Height is set optically per tier; MINT's master sits on a light plate on the dark stage.
 */
const LOGO_HEIGHT: Record<ProductGroup['tier'], number> = { flagship: 40, major: 34, independent: 30, specialisation: 26, selected: 26 };

export function spokePath(anchor: Anchor): string {
  const angle = Math.atan2(anchor.y - ECO.cy, anchor.x - ECO.cx);
  const start = ECO.coreRadius + 18;
  const end = Math.hypot(anchor.x - ECO.cx, anchor.y - ECO.cy) - 42;
  const p = (r: number) => `${(ECO.cx + Math.cos(angle) * r).toFixed(1)} ${(ECO.cy + Math.sin(angle) * r).toFixed(1)}`;
  return `M${p(start)} L${p(end)}`;
}

export function orbitPath(): string {
  const { cx, cy, rx, ry } = ECO;
  return `M${cx - rx} ${cy} A${rx} ${ry} 0 1 1 ${cx + rx} ${cy} A${rx} ${ry} 0 1 1 ${cx - rx} ${cy}`;
}

/** MINT carries its enabled specialisations with it, as a small trunk of named branches. */
function specialisations(context: PresentationContext): string {
  const list = context.products.filter((p) => p.tier === 'specialisation');
  if (!list.length) return '';
  const x = -58;
  const ys = list.map((_, i) => 96 + i * 34);
  return `
    <g class="world__family">
      <path class="motif motif--faint draw" pathLength="1" d="M${x} 34 V${ys[ys.length - 1]}"/>
      ${list.map((p, i) => `
        <path class="motif draw" pathLength="1" d="M${x} ${ys[i] - 14} q0 14 14 14"/>
        <circle class="motif-dot" cx="${x + 18}" cy="${ys[i]}" r="4"/>
        <text class="world__branch" x="${x + 32}" y="${ys[i] + 5}">${p.name}</text>`).join('')}
    </g>`;
}

function world(group: ProductGroup, anchor: Anchor, from: number, context: PresentationContext): string {
  const lead = products[group.lead];
  const flagship = group.tier === 'flagship';
  const selected = group.tier === 'selected';
  const h = LOGO_HEIGHT[group.tier];
  // Lockups sit on the far side of the anchor so the spoke from the core never crosses them; the
  // flagship keeps its lockup above so its specialisations can hang below it.
  const upper = flagship || anchor.y < ECO.cy - 1;
  const logoY = flagship ? -58 : upper ? -54 : 62;
  const essenceY = logoY + h / 2 + 30;
  return `
    <g class="world world--${group.tier}" data-group="${group.id}" data-from="${from}" style="--family:${group.color}">
      <g transform="translate(${anchor.x.toFixed(1)} ${anchor.y.toFixed(1)})">
        <g class="world__motion">
          <circle class="world__glow" r="${flagship ? 60 : selected ? 30 : 40}"/>
          <circle class="world__dot" r="5"/>
          ${selected ? `<text class="world__tier" y="${logoY - h / 2 - 16}" text-anchor="middle">SELECTED WORK</text>` : ''}
          ${brandLogoSvg(lead.brand, { x: 0, y: logoY, height: h, surface: 'dark' })}
          <path class="motif draw" pathLength="1" d="M-26 ${essenceY - 18} H26"/>
          <text class="world__essence" y="${essenceY}" text-anchor="middle">${lead.essence}</text>
          ${flagship ? specialisations(context) : ''}
        </g>
      </g>
    </g>`;
}

/**
 * Renders the ecosystem as SVG markup for a 1600×900 viewBox.
 * `reveal` returns the fragment step on which each group (and its spoke) appears.
 */
export function renderEcosystem(context: PresentationContext, reveal: (group: ProductGroup) => number): string {
  const layout = layoutGroups(context.groups);
  const spokes = context.groups.map((g) =>
    `<path class="eco-spoke draw" data-group="${g.id}" data-from="${reveal(g)}" pathLength="1" d="${spokePath(layout.get(g.id)!)}" style="--family:${g.color}"/>`).join('');
  const pulses = context.groups.map((g) =>
    `<circle class="eco-pulse" data-group="${g.id}" r="4" style="--family:${g.color}"/>`).join('');

  return `
    <path class="eco-orbit" d="${orbitPath()}"/>
    <path class="eco-orbit eco-orbit--lit draw" pathLength="1" d="${orbitPath()}"/>
    <g class="eco-spokes">${spokes}</g>
    <g class="eco-pulses">${pulses}</g>
    <g class="eco-core">
      <circle class="eco-core__spin" cx="${ECO.cx}" cy="${ECO.cy}" r="${ECO.coreRadius + 16}"/>
      <circle class="eco-core__ring" cx="${ECO.cx}" cy="${ECO.cy}" r="${ECO.coreRadius}"/>
      ${brandLogoSvg('moderta', { x: ECO.cx, y: ECO.cy, height: 40, surface: 'dark', className: 'eco-core__logo' })}
    </g>
    ${context.groups.map((g) => world(g, layout.get(g.id)!, reveal(g), context)).join('')}`;
}

/**
 * The order in which the ecosystem is revealed: the flagship on its own, then independent
 * platforms and products, then selected work. Empty phases are dropped.
 */
export function ecosystemPhases(context: PresentationContext): ProductGroup[][] {
  const g = context.groups;
  return [
    g.filter((x) => x.tier === 'flagship'),
    g.filter((x) => x.tier === 'major' || x.tier === 'independent'),
    g.filter((x) => x.tier === 'selected'),
  ].filter((phase) => phase.length > 0);
}
